import { CallMapper } from '../../../common/calls/mappers/CallMapper';
import { Call } from '../../../common/calls/models/Call';
import { CALLS_FRAGMENT } from '../../../common/calls/services/CallRepository';
import { InvalidUrlError } from '../../../common/errors/errors';
import { SshHandshakeMapper } from '../../../common/handshakes/mappers/SshHandshakeMapper';
import { TlsHandshakeMapper } from '../../../common/handshakes/mappers/TlsHandshakeMapper';
import { SshHandshake } from '../../../common/handshakes/models/SshHandshake';
import { TlsHandshake } from '../../../common/handshakes/models/TlsHandshake';
import { SSH_HANDSHAKE_FRAGMENT } from '../../../common/handshakes/services/SshHandshakesRepository';
import { TLS_HANDSHAKE_FRAGMENT } from '../../../common/handshakes/services/TlsHandshakesRepository';
import { InstanceMapper } from '../../../common/instances/mappers/InstanceMapper';
import { Instance } from '../../../common/instances/models/Instance';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, DEFAULT_PAGE_INFO, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { INSTANCE_QUERY_FRAGMENT } from '../../issues/issue/services/ReportIssueRepository';
import { OperationDetailMapper } from '../mappers/OperationDetailMapper';
import { OperationKeyMapper } from '../mappers/OperationKeyMapper';
import { OperationMapper } from '../mappers/OperationMapper';
import { Operation } from '../models/Operation';
import { OperationDetail } from '../models/OperationDetail';
import { OperationKey } from '../models/valueObjects/OperationKey';

export const DEFAULT_OPERATION_CALLS_PAGE_SIZE = 5;
export const DEFAULT_OPERATION_KEYS_PAGE_SIZE = 5;
export const DEFAULT_OPERATION_INSTANCES_PAGE_SIZE = 5;

const INSTANCE_FRAGMENT = `
totalCount
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    ${INSTANCE_QUERY_FRAGMENT}
    rule {
      number
    }
  }
}
`;

const KEYS_FRAGMENT = `
totalCount
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  role
  cipherData
  wrappedByKeyId
  wrappedKeyId
  node {
    id
    shortDescription
  }
}
`;

const TLS_HANDSHAKES_FRAGMENT = `
totalCount
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    ${TLS_HANDSHAKE_FRAGMENT}
  }
}
`;

export const GET_REPORT_OPERATIONS_QUERY = `
query (
  $reportId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: OperationsFilter,
  $sorters: [OperationsSorter]
) {
  node(id: $reportId) {
    ... on ReportDone {
      operations(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sorters: $sorters
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            type
            context
            algorithms
            timestamp
          }
        }
        totalCount
      }
    }
  }
}
`;

export const GET_OPERATION_QUERY = `
query($operationId: ID!) {
  node(id: $operationId) {
    ... on Operation {
      id
      type
      context
      algorithms
      timestamp
      valueJson
      firstCallSite {
        class
        method
        file
        line
      }
      tlsHandshake {
        ${TLS_HANDSHAKES_FRAGMENT}
      }
      sshHandshake {
        ${SSH_HANDSHAKE_FRAGMENT}
      }
      report {
        id
        traceType
        project {
          id
        }
      }
      calls(first: ${DEFAULT_OPERATION_CALLS_PAGE_SIZE}) {
        ${CALLS_FRAGMENT}
      }
      keys(first: ${DEFAULT_OPERATION_KEYS_PAGE_SIZE}) {
        ${KEYS_FRAGMENT}
      }
      instances(first: ${DEFAULT_OPERATION_INSTANCES_PAGE_SIZE}, sorters: [{field: severity, order: DESC}]) {
        ${INSTANCE_FRAGMENT}
      }
    }
  }
}
`;

export const GET_OPERATION_INSTANCES_QUERY = `
query(
  $id: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String
) {
  node(id: $id) {
    ... on Operation {
      instances(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        sorters: [{field: severity, order: DESC}]
      ) {
        ${INSTANCE_FRAGMENT}
      }
    }
  }
}
`;

export const GET_OPERATION_KEYS_QUERY = `
query(
  $id: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String
) {
  node(id: $id) {
    ... on Operation {
      keys(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
      ) {
        ${KEYS_FRAGMENT}
      }
    }
  }
}
`;

type OperationData = {
  operation: OperationDetail;
  initialTlsHandshakes: PaginationResult<TlsHandshake>;
  initialSshHandshakes: PaginationResult<SshHandshake>;
  initialCalls: PaginationResult<Call>;
  initialInstances: PaginationResult<Instance>;
  initialKeys: PaginationResult<OperationKey>;
};

export class OperationRepository extends BaseGQLService {
  private readonly mapper = new OperationMapper();
  private readonly detailMapper = new OperationDetailMapper();
  private readonly callMapper = new CallMapper();
  private readonly tlsHandshakeMapper = new TlsHandshakeMapper();
  private readonly sshHandshakeMapper = new SshHandshakeMapper();
  private readonly instanceMapper = new InstanceMapper();
  private readonly operationKeyMapper = new OperationKeyMapper();

  async getList(reportId: number, cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<Operation>> {
    return this.query(
      GET_REPORT_OPERATIONS_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ...this.mapCursorInfo(cursor),
        filters,
        sorters,
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.node.operations;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getOperation(reportId: number, operationId: number): Promise<OperationData> {
    return this.query(
      GET_OPERATION_QUERY,
      {
        operationId: this.gqlIdEncoder.encode(ModelType.Operation, operationId),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not Operation ${operationId}`);
        }
        if (!data.node.report || !data.node.report.id || data.node.report.id !== this.gqlIdEncoder.encode(ModelType.Report, reportId)) {
          throw new InvalidUrlError(`Invalid request: Operation ${operationId} does not belong to Report ${reportId}`);
        }
        const { calls, instances, tlsHandshake, sshHandshake, keys } = data.node;
        return {
          operation: this.detailMapper.mapGQLModel(data.node),
          initialTlsHandshakes: {
            data: tlsHandshake.edges.map(({ node }: any) => this.tlsHandshakeMapper.mapGQLModel(node)),
            pageInfo: tlsHandshake.pageInfo,
            totalCount: tlsHandshake.totalCount,
          },
          initialSshHandshakes: {
            data: sshHandshake ? [this.sshHandshakeMapper.mapGQLModel(sshHandshake)] : [],
            pageInfo: DEFAULT_PAGE_INFO,
            totalCount: sshHandshake ? 1 : 0,
          },
          initialCalls: {
            data: calls.edges.map(({ node }: any) => this.callMapper.mapGQLModel(node)),
            pageInfo: calls.pageInfo,
            totalCount: calls.totalCount,
          },
          initialInstances: {
            data: instances.edges.map(({ node }: any) => this.instanceMapper.mapGQLModel({ ...node, ruleNumber: node.rule.number })),
            pageInfo: instances.pageInfo,
            totalCount: instances.totalCount,
          },
          initialKeys: {
            data: keys.edges.map((edge: any) => this.operationKeyMapper.mapGQLModel(edge)),
            pageInfo: keys.pageInfo,
            totalCount: keys.totalCount,
          },
        };
      }
    );
  }

  async getOperationInstances(operationId: number, cursor: CursorInfo): Promise<PaginationResult<Instance>> {
    return this.query(
      GET_OPERATION_INSTANCES_QUERY,
      {
        id: this.gqlIdEncoder.encode(ModelType.Operation, operationId),
        ...this.mapCursorInfo(cursor),
      },
      (data) => {
        const { instances } = data.node;
        return {
          data: instances.edges.map(({ node }: any) => this.instanceMapper.mapGQLModel({ ...node, ruleNumber: node.rule.number })),
          pageInfo: instances.pageInfo,
          totalCount: instances.totalCount,
        };
      }
    );
  }

  async getOperationKeys(operationId: number, cursor: CursorInfo): Promise<PaginationResult<OperationKey>> {
    return this.query(
      GET_OPERATION_KEYS_QUERY,
      {
        id: this.gqlIdEncoder.encode(ModelType.Operation, operationId),
        ...this.mapCursorInfo(cursor),
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.node.keys;
        return {
          data: edges.map((edge: any) => this.operationKeyMapper.mapGQLModel(edge)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
